import { Switch } from "@heroui/react";
import { Select, SelectItem } from "@heroui/react";
import { useEffect, useState } from "react";

// Local Imports
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useNotificationPreferences, useUpdateNotificationPreferences } from "../../../lib/react-query/notifications";
import { useClientProjectContext } from "../../../hooks/useClientRole";
import { useUpdateProfileDetails } from "../../../lib/react-query/profile";
import { HiOutlineViewColumns } from "react-icons/hi2";
import { BellIcon, EditProfileIcon, PersonIcon, SecurityIcon } from "../../../assets/icons";
import { CustomModal, Divider, ProfileCard, showToast, TextInput } from "../../../components/shared";
import { resetPasswordFunction } from "../../../redux/reducers/authReducers/authReducers";
import { isEmpty, isError, validatePassword } from "../../../utils/validations";
import { COLORS } from "../../../constants/colors";

const AccountSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading } = useSelector((state) => state.user);
  const { profileDetails, isClientByRole } = useClientProjectContext();
  const { data: preferences, error: notificationError, refetch: refetchPreferences } = useNotificationPreferences();
  const { mutateAsync: savePreferences, isLoading: savingPreferences } = useUpdateNotificationPreferences();
  const { mutateAsync: saveProfileDetails, isPending: savingWorkspaceMode } = useUpdateProfileDetails();
  const [openSecurityModal, setOpenSecurityModal] = useState(false);
  const [openNotificationModal, setOpenNotificationModal] = useState(false);
  const [clientModeEnabled, setClientModeEnabled] = useState(true);
  const [passwordCriteria, setPasswordCriteria] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  // Form Data
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Errors State
  const [formErrors, setFormErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Notification Preferences State
  const [notificationPreferences, setNotificationPreferences] = useState({
    chat_delay_minutes: 60,
  });

  useEffect(() => {
    setClientModeEnabled(profileDetails?.client_mode_enabled !== false);
  }, [profileDetails]);

  // Delay options for the dropdown
  const delayOptions = [
    { id: "60", name: "1 hour" },
    { id: "120", name: "2 hours" },
    { id: "240", name: "4 hours" },
    { id: "1440", name: "24 hours" },
  ];

  // Handle Password Change
  const handlePasswordChange = (event) => {
    const { name, value } = event.target;

    if (name === "newPassword") {
      setPasswordCriteria({
        minLength: value.length >= 8,
        hasUppercase: /[A-Z]/.test(value),
        hasLowercase: /[a-z]/.test(value),
        hasNumber: /[0-9]/.test(value),
        hasSpecialChar: /[!@#$%^&*()_\-+=<>?{}[\]~`|\\:;"',./]/.test(value),
      });
    }

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));

    setPasswords((prevPasswords) => ({
      ...prevPasswords,
      [name]: value,
    }));
  };

  // Handle Close Modal
  const handleCloseModal = () => {
    setOpenSecurityModal(false);
    setPasswords({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setFormErrors({});
  };

  // Handle Reset Password
  const handleResetPassword = async () => {
    if (!passwords?.currentPassword) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        currentPassword: "Current password is required",
      }));
    }

    if (!validatePassword(passwords?.newPassword)) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        newPassword: "Invalid Password. Please ensure your password meets the following criteria.",
      }));
    }

    if (!validatePassword(passwords?.confirmPassword)) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        confirmPassword: "Invalid Password. Please ensure your password meets the following criteria.",
      }));
    }

    if (passwords?.newPassword !== passwords?.confirmPassword) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        confirmPassword: "Invalid Password. Please ensure your password meets the following criteria.",
      }));
      return;
    }

    if (isError(formErrors)) {
      return;
    }

    const data = await dispatch(
      resetPasswordFunction({
        current_password: passwords?.currentPassword,
        new_password: passwords?.newPassword,
      })
    );

    if (data?.meta?.requestStatus === "rejected") {
      showToast(`${data?.payload ? data?.payload[0]?.current_password : "Something went wrong"}`, "error", {
        position: "top-center",
      });
    }

    if (data?.meta?.requestStatus === "fulfilled") {
      showToast("Password updated successfully", "success", {
        position: "top-center",
      });
      handleCloseModal();
    }
  };

  const checkPasswordCriteria = () => {
    if (
      passwordCriteria?.hasLowercase &&
      passwordCriteria?.hasUppercase &&
      passwordCriteria?.hasNumber &&
      passwordCriteria?.hasSpecialChar &&
      passwordCriteria?.minLength
    ) {
      return true;
    }
    return false;
  };

  // Notification Preferences Handlers
  const handleOpenNotificationModal = () => {
    refetchPreferences();
    setOpenNotificationModal(true);
  };

  const handleCloseNotificationModal = () => {
    setOpenNotificationModal(false);
    setNotificationPreferences({
      chat_delay_minutes: preferences?.chat_delay_minutes ?? 60,
    });
  };

  const handleSaveNotificationPreferences = async () => {
    try {
      await savePreferences(notificationPreferences);
      showToast("Notification preferences updated successfully!", "success", {
        position: "top-center",
      });
      setOpenNotificationModal(false);
    } catch (err) {
      // ignore
    }
  };

  const handleClientModeChange = async (nextValue) => {
    const previousValue = clientModeEnabled;
    setClientModeEnabled(nextValue);

    try {
      await saveProfileDetails({ client_mode_enabled: nextValue });
      showToast(
        nextValue
          ? "Client mode enabled"
          : "Standard mode enabled",
        "success",
        {
          position: "top-center",
        }
      );
    } catch (err) {
      setClientModeEnabled(previousValue);
      showToast("Failed to update workspace mode", "error", {
        position: "top-center",
      });
    }
  };

  // Load notification preferences when they're fetched
  useEffect(() => {
    if (preferences) {
      setNotificationPreferences({
        chat_delay_minutes: preferences.chat_delay_minutes ?? 60,
      });
    }
  }, [preferences]);

  // Show notification error toast
  useEffect(() => {
    if (notificationError) {
      showToast(notificationError.message || "Failed to load preferences", "error", { position: "top-center" });
    }
  }, [notificationError]);

  return (
    <div className="min-h-screen p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary">Account Settings</h1>
          <p className="text-mediumgrey mt-1">
            Edit personal info, password, and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
          <ProfileCard
          icon={<EditProfileIcon color={COLORS.grey} />}
          iconSX="bg-surface"
          title="Edit Personal Details"
          text="Update personal and professional details"
          onClick={() => navigate("/profile/edit")}
        />
        <ProfileCard
          icon={<SecurityIcon color={COLORS.warning} />}
          iconSX="bg-warning/10"
          title="Edit Password"
          text="Manage your security settings"
          onClick={() => setOpenSecurityModal(true)}
        />
        <ProfileCard
          icon={<BellIcon color={COLORS.grey} />}
          iconSX="bg-surface"
          title="Notification Preferences"
          text="Configure when you receive email notifications for unread messages or requests"
          onClick={handleOpenNotificationModal}
        />
        <ProfileCard
          icon={<PersonIcon color={COLORS.grey} />}
          iconSX="bg-surface"
          title="Profile Hub"
          text="View your full profile, experience, and credentials"
          onClick={() => navigate("/profile")}
        />
      </div>

      {isClientByRole && (
        <div className="rounded-2xl border border-surface-border bg-white p-6 shadow-sm mb-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center gap-x-6">
            <div className="flex gap-3">
              <div className="w-9 h-9 rounded-lg bg-surface flex items-center justify-center shrink-0">
                <HiOutlineViewColumns className="w-5 h-5 text-mediumgrey" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-primary">Workspace mode</h3>
                <p className="mt-1 text-sm text-mediumgrey">
                  Guided mode provides a simplified view focused on reviewing your invited project&apos;s progress. Standard mode gives access to the full workspace.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 md:justify-end shrink-0">
              <div className="text-right">
                <p className="text-sm font-medium text-grey whitespace-nowrap">
                  {clientModeEnabled ? "Guided mode on" : "Standard workspace on"}
                </p>
              </div>

              <Switch
                aria-label="Toggle client mode"
                isDisabled={savingWorkspaceMode}
                isSelected={clientModeEnabled}
                onValueChange={handleClientModeChange}
                size="sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      <CustomModal
        title={"Edit Password"}
        open={openSecurityModal}
        close={handleCloseModal}
        primaryButtonClick={handleResetPassword}
        secondaryButtonClick={handleCloseModal}
        primaryButtonText={"Update"}
        primaryButtonLoading={loading}
        primaryButtonDisabled={loading ? true : isEmpty(passwords) || isError(formErrors)}
      >
        <div className="font-sans pb-2">
          <h3 className="text-base font-semibold text-darkgrey">Change Password</h3>
          <Divider className="mt-2" />
        </div>

        <form className="flex flex-col gap-4 mb-2" onSubmit={handleResetPassword}>
          <TextInput
            label="Current Password"
            type="password"
            name="currentPassword"
            value={passwords?.currentPassword}
            onChange={handlePasswordChange}
            error={formErrors?.currentPassword}
            errorMsg={formErrors?.currentPassword}
          />
          <TextInput
            label="New Password"
            type="password"
            name="newPassword"
            value={passwords.newPassword}
            onChange={handlePasswordChange}
            error={formErrors?.newPassword}
            errorMsg={formErrors?.newPassword}
            description={checkPasswordCriteria() ? "Password Strength: Strong" : "Password Strength: Weak"}
          />
          <TextInput
            label="Confirm New Password"
            type="password"
            name="confirmPassword"
            value={passwords.confirmPassword}
            onChange={handlePasswordChange}
            error={formErrors?.confirmPassword}
            errorMsg={formErrors?.confirmPassword}
          />
          <div className="mt-1">
            <p className="text-xs font-medium text-grey mb-2">Password Requirements</p>
            <ul className="text-xs select-none grid grid-cols-1 xs:grid-cols-2 gap-1.5">
              <li className="text-grey text-sm vertical-center gap-2">
                <div className={`list-bullet ${passwordCriteria?.minLength ? "bg-success" : "bg-danger"}`} />
                Min 8 characters
              </li>
              <li className="text-grey text-sm vertical-center gap-2">
                <div className={`list-bullet ${passwordCriteria?.hasUppercase ? "bg-success" : "bg-danger"}`} />
                One uppercase letter
              </li>
              <li className="text-grey text-sm vertical-center gap-2">
                <div className={`list-bullet ${passwordCriteria?.hasLowercase ? "bg-success" : "bg-danger"}`} />
                One lowercase letter
              </li>
              <li className="text-grey text-sm vertical-center gap-2">
                <div className={`list-bullet ${passwordCriteria?.hasNumber ? "bg-success" : "bg-danger"}`} />
                One number
              </li>
              <li className="text-grey text-sm vertical-center gap-2">
                <div className={`list-bullet ${passwordCriteria?.hasSpecialChar ? "bg-success" : "bg-danger"}`} />
                One special character
              </li>
            </ul>
          </div>
        </form>
      </CustomModal>

      {/* Notification Preferences Modal */}
      <CustomModal
        title={"Notification Preferences"}
        open={openNotificationModal}
        close={handleCloseNotificationModal}
        primaryButtonClick={handleSaveNotificationPreferences}
        secondaryButtonClick={handleCloseNotificationModal}
        primaryButtonText={"Save Changes"}
        secondaryButtonText={"Cancel"}
        primaryButtonLoading={savingPreferences}
        primaryButtonDisabled={savingPreferences}
      >
        <div className="font-sans pb-2">
          <h3 className="text-base font-semibold text-darkgrey">Email Notification Settings</h3>
          <Divider className="mt-2" />
        </div>

        <div className="flex flex-col gap-3 mb-2 mt-3">
          <Select
            label="Email Notification Delay"
            placeholder="Select delay time"
            aria-label="Email Notification Delay"
            selectedKeys={[notificationPreferences.chat_delay_minutes?.toString() || "60"]}
            onSelectionChange={(value) => {
              const selected = parseInt(Array.from(value)[0]);
              if (!Number.isNaN(selected)) {
                setNotificationPreferences((prev) => ({
                  ...prev,
                  chat_delay_minutes: selected,
                }));
              }
            }}
            className="w-full"
            classNames={{
              trigger: "h-[48px] bg-surface shadow-none rounded-lg",
              popoverContent: "rounded-lg bg-white border border-surface-border/50 overflow-hidden",
              listboxWrapper: "rounded-lg max-h-48 md overflow-y-auto",
              listbox: "rounded-lg",
            }}
            popoverProps={{
              isDismissable: true,
              placement: "bottom-start",
              classNames: {
                base: "bg-transparent",
                content: "rounded-lg bg-white border border-surface-border/50 overflow-hidden",
              },
            }}
            description="Choose how long DealNest should wait before sending you an email for unread messages."
          >
            {delayOptions.map((opt) => (
              <SelectItem key={opt.id} value={opt.id}>
                {opt.name}
              </SelectItem>
            ))}
          </Select>
        </div>
      </CustomModal>
    </div>
    </div>
  );
};

export default AccountSettings;
